import { useEffect, useState, useCallback } from "react";
import { GoogleLogo, PlugsConnected, Plugs, ArrowsClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import { adsStatus, adsOauthStart, adsDisconnect, errText } from "@/lib/api";

export const GoogleAdsBadge = ({ onChange }) => {
    const [status, setStatus] = useState(null); // { configured, connected }
    const [busy, setBusy] = useState(false);

    const refresh = useCallback(async () => {
        try {
            const s = await adsStatus();
            setStatus(s);
            onChange?.(s);
        } catch {
            setStatus({ configured: false, connected: false });
        }
    }, [onChange]);

    useEffect(() => {
        refresh();
    }, [refresh]);

    // If we just came back from an OAuth callback, the URL has ?ads_connected=1 or ?ads_error=...
    useEffect(() => {
        const params = new URLSearchParams(window.location.search);
        if (params.get("ads_connected") === "1") {
            toast.success("Google Ads connected");
            window.history.replaceState({}, "", "/");
            refresh();
        } else if (params.get("ads_error")) {
            toast.error(`Google Ads connect failed: ${params.get("ads_error")}`);
            window.history.replaceState({}, "", "/");
        }
    }, [refresh]);

    const handleConnect = async () => {
        setBusy(true);
        try {
            const { url } = await adsOauthStart();
            window.location.href = url;
        } catch (e) {
            const msg = errText(e, "Failed to start connection");
            toast.error(msg);
            setBusy(false);
        }
    };

    const handleDisconnect = async () => {
        setBusy(true);
        try {
            await adsDisconnect();
            toast.success("Google Ads disconnected");
            await refresh();
        } catch (e) {
            toast.error(errText(e, "Disconnect failed"));
        } finally {
            setBusy(false);
        }
    };

    if (!status) {
        return (
            <div className="flex items-center gap-2 text-[10px] font-mono text-neutral-400">
                <ArrowsClockwise size={12} weight="bold" className="animate-spin" />
                Checking Ads…
            </div>
        );
    }

    if (!status.configured) {
        return (
            <div className="text-[10px] font-mono text-neutral-400 flex items-center gap-1.5">
                <Plugs size={12} weight="bold" />
                Google Ads not set up
            </div>
        );
    }

    if (status.connected) {
        return (
            <button
                data-testid="btn-ads-disconnect"
                onClick={handleDisconnect}
                disabled={busy}
                title={`Connected to MCC ${status.mcc_id}`}
                className="w-full flex items-center gap-2 px-2 h-8 border border-emerald-600 bg-emerald-50 text-emerald-800 font-mono uppercase tracking-widest text-[10px] font-bold hover:bg-emerald-600 hover:text-white transition"
            >
                <PlugsConnected size={12} weight="bold" />
                <span className="flex-1 text-left truncate">Ads Connected</span>
                <span className="text-[9px] opacity-70">disconnect</span>
            </button>
        );
    }

    return (
        <button
            data-testid="btn-ads-connect"
            onClick={handleConnect}
            disabled={busy}
            className="w-full flex items-center gap-2 px-2 h-8 border border-ink bg-white text-ink hover:bg-ink hover:text-white font-mono uppercase tracking-widest text-[10px] font-bold transition"
        >
            <GoogleLogo size={12} weight="bold" />
            <span className="flex-1 text-left">Connect Google Ads</span>
        </button>
    );
};
